import { useEffect, useState } from "react";
import { RefreshCw, ExternalLink, Youtube } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";
import { AField, Btn, PageHead, inputCls, fmtDate } from "../components";

export default function Latest() {
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (refresh = false) =>
    adminApi.get(`/admin/youtube/latest${refresh ? "?refresh=true" : ""}`).then((r) => {
      setData(r.data);
      setChannel(r.data.channel || "");
    }).catch(() => {});

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    await load(true);
    setBusy(false);
    toast.success("Checked the channel for new episodes.", { id: "yt-refresh" });
  };

  const saveChannel = async () => {
    await adminApi.put("/admin/config", { youtube_channel: channel });
    toast.success("Channel updated — re-resolving latest episode.", { id: "yt-channel" });
    load(true);
  };

  const v = data?.video;

  return (
    <div data-testid="admin-latest">
      <PageHead title="LATEST / YOUTUBE" sub="The public Latest section auto-surfaces the newest episode from the channel — checked hourly, no manual swaps.">
        <Btn variant="ghost" onClick={refresh} disabled={busy} data-testid="latest-refresh">
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {busy ? "CHECKING..." : "REFRESH NOW"}
        </Btn>
      </PageHead>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-woodborder bg-card/40 p-6">
          <p className="font-mono text-[10px] tracking-[0.25em] text-tan mb-4">CHANNEL SOURCE</p>
          <AField label="YOUTUBE CHANNEL URL">
            <input className={inputCls} value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="latest-channel-input" />
          </AField>
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <Btn onClick={saveChannel} data-testid="latest-channel-save">SAVE CHANNEL</Btn>
            <span className="font-mono text-[10px] tracking-[0.15em] text-clay" data-testid="latest-channel-id">
              CHANNEL ID: {data?.channel_id || "RESOLVING..."}
            </span>
          </div>
        </div>

        <div className="border border-woodborder bg-card/40 p-6" data-testid="latest-video-card">
          <p className="font-mono text-[10px] tracking-[0.25em] text-tan mb-4">CURRENT LATEST EPISODE</p>
          {v ? (
            <div>
              <div className="relative border border-woodborder overflow-hidden group">
                <img src={v.thumbnail} alt={v.title} className="w-full aspect-video object-cover" />
                <a href={v.url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-obsidian/0 hover:bg-obsidian/40 transition-colors" data-testid="latest-video-link">
                  <span className="w-14 h-14 rounded-full bg-burnt flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink size={18} className="text-cream" />
                  </span>
                </a>
              </div>
              <h3 className="mt-4 font-editorial font-bold text-cream leading-snug" data-testid="latest-video-title">{v.title}</h3>
              <p className="mt-1.5 font-mono text-[10px] tracking-[0.15em] text-clay">PUBLISHED {fmtDate(v.published)?.toUpperCase()}</p>
              {v.description && <p className="mt-3 text-sand text-sm font-light line-clamp-3">{v.description}</p>}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Youtube size={32} className="text-clay" />
              <p className="mt-4 font-mono text-xs text-clay" data-testid="latest-empty">NO EPISODE PULLED YET — SAVE THE CHANNEL AND REFRESH.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
